import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useMemo, useState } from 'react'
import { CheckCircle2, FileText, Loader2, Lock, MessageSquare, Save } from 'lucide-react'
import {
  createPublicShareComment,
  fetchPublicShareData,
  patchPublicShareField,
} from '@/api/public-share'
import type { components } from '@/api/schema'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Progress } from '@/components/ui/progress'
import { formatError } from '@/lib/format-error'

export const Route = createFileRoute('/public/docfiller/$shareToken')({
  component: PublicDocfillerSharePage,
})

type PublicShareDocumentRead = components['schemas']['PublicShareDocumentRead']
type FieldRead = components['schemas']['FieldRead']
type DocumentAccessEventRead = components['schemas']['DocumentAccessEventRead']

type PublicShareStatus = 'loading' | 'ready' | 'unavailable'

function fieldLabel(field: FieldRead): string {
  return field.friendly_name || field.name
}

function editableFieldType(field: FieldRead): string {
  if (field.kind === 'date') return 'date'
  if (field.semantic_type === 'email') return 'email'
  return 'text'
}

function commentBody(event: DocumentAccessEventRead): string {
  const body = event.event_metadata?.body
  return typeof body === 'string' ? body : ''
}

function expiredMessage(message: string | null): string {
  if (!message) return 'This signing link is unavailable.'
  return /expired|deadline/i.test(message)
    ? 'This signing link has expired. Contact the sender for a new deadline or link.'
    : message
}

export function PublicDocfillerSharePage() {
  const { shareToken } = Route.useParams()
  const [status, setStatus] = useState<PublicShareStatus>('loading')
  const [payload, setPayload] = useState<PublicShareDocumentRead | null>(null)
  const [values, setValues] = useState<Record<string, string>>({})
  const [comment, setComment] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [savingField, setSavingField] = useState<string | null>(null)
  const [savingComment, setSavingComment] = useState(false)

  useEffect(() => {
    let cancelled = false
    fetchPublicShareData(shareToken)
      .then((response) => {
        if (cancelled) return
        setPayload(response)
        setValues(
          Object.fromEntries(
            response.document.fields.map((field) => [field.uuid, field.current_value ?? '']),
          ),
        )
        setError(null)
        setStatus('ready')
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setPayload(null)
        setError(formatError(err))
        setStatus('unavailable')
      })
    return () => {
      cancelled = true
    }
  }, [shareToken])

  const assignedFields = useMemo(() => {
    if (!payload) return []
    const partyUuid = payload.party?.uuid ?? null
    if (!partyUuid) return payload.document.fields
    return payload.document.fields.filter(
      (field) => field.party_uuid === partyUuid || field.party_uuid === null,
    )
  }, [payload])

  const completion = useMemo(() => {
    if (assignedFields.length === 0) return 0
    const complete = assignedFields.filter((field) => values[field.uuid]?.trim()).length
    return Math.round((complete / assignedFields.length) * 100)
  }, [assignedFields, values])

  const canEdit = payload?.role !== 'viewer'

  const saveField = async (field: FieldRead) => {
    if (!canEdit) return
    setSavingField(field.uuid)
    try {
      const updated = await patchPublicShareField(shareToken, field.uuid, {
        current_value: values[field.uuid] ?? '',
        accepted_by: field.kind === 'signature' ? 'user' : undefined,
      })
      setPayload((current) => {
        if (!current) return current
        return {
          ...current,
          document: {
            ...current.document,
            fields: current.document.fields.map((item) =>
              item.uuid === updated.uuid ? updated : item,
            ),
          },
        }
      })
      setError(null)
    } catch (err) {
      setError(formatError(err))
    } finally {
      setSavingField(null)
    }
  }

  const submitComment = async (e: React.FormEvent) => {
    e.preventDefault()
    const body = comment.trim()
    if (!body) return
    setSavingComment(true)
    try {
      const created = await createPublicShareComment(shareToken, { body })
      setPayload((current) =>
        current ? { ...current, comments: [created, ...(current.comments ?? [])] } : current,
      )
      setComment('')
      setError(null)
    } catch (err) {
      setError(formatError(err))
    } finally {
      setSavingComment(false)
    }
  }

  return (
    <main className="min-h-screen bg-background px-4 py-6 text-foreground">
      <div className="mx-auto max-w-5xl space-y-4">
        <header className="flex flex-col gap-3 border-b border-border pb-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <p className="flex items-center gap-2 text-xs font-medium uppercase text-muted-foreground">
              <FileText className="size-4" aria-hidden />
              Recipient document
            </p>
            <h1 className="truncate text-2xl font-semibold">
              {payload?.document.source_filename ?? 'Shared document'}
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {payload?.party?.name || payload?.party?.email || 'Public recipient'}
            </p>
          </div>
          <Badge tone={canEdit ? 'success' : 'outline'} size="sm">
            {payload?.party?.role ?? payload?.role ?? 'loading'}
          </Badge>
        </header>

        <Card className="space-y-3 p-4" data-testid="public-share-flow">
          <div className="flex items-center justify-between text-sm">
            <span>{status === 'ready' ? 'Assigned fields' : 'Loading link'}</span>
            <span className="text-muted-foreground">{completion}%</span>
          </div>
          <Progress percent={completion} />
          {status === 'loading' && (
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" aria-hidden />
              Loading link details...
            </p>
          )}
          {status === 'unavailable' && (
            <p className="flex items-center gap-2 text-sm text-destructive" role="alert">
              <Lock className="size-4" aria-hidden />
              {expiredMessage(error)}
            </p>
          )}
          {status === 'ready' && (
            <p className="flex items-center gap-2 text-sm text-success">
              <CheckCircle2 className="size-4" aria-hidden />
              Link opened and tracked.
            </p>
          )}
          {status === 'ready' && payload?.expires_at && (
            <p className="text-xs text-muted-foreground">
              Signing deadline: {new Date(payload.expires_at).toLocaleString()}
            </p>
          )}
        </Card>

        {status === 'ready' && (
          <div className="grid gap-4 lg:grid-cols-[minmax(0,_1fr)_320px]">
            <section className="space-y-3" data-testid="assigned-fields">
              {assignedFields.length === 0 ? (
                <p className="rounded-md border border-border bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
                  No fields are assigned to this recipient.
                </p>
              ) : (
                assignedFields.map((field) => (
                  <div
                    key={field.uuid}
                    className="rounded-md border border-border bg-card p-3"
                    data-testid={`assigned-field-${field.uuid}`}
                  >
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <label className="text-sm font-medium" htmlFor={`field-${field.uuid}`}>
                        {fieldLabel(field)}
                      </label>
                      <Badge tone={field.kind === 'signature' ? 'primary' : 'outline'} size="xs">
                        {field.kind}
                      </Badge>
                    </div>
                    <div className="flex flex-col gap-2 sm:flex-row">
                      <Input
                        id={`field-${field.uuid}`}
                        type={editableFieldType(field)}
                        value={values[field.uuid] ?? ''}
                        onChange={(e) =>
                          setValues((current) => ({
                            ...current,
                            [field.uuid]: e.target.value,
                          }))
                        }
                        disabled={!canEdit || savingField === field.uuid}
                        data-testid={`field-input-${field.uuid}`}
                      />
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => saveField(field)}
                        disabled={!canEdit || savingField === field.uuid}
                        data-testid={`field-save-${field.uuid}`}
                      >
                        {savingField === field.uuid ? (
                          <Loader2 className="animate-spin" />
                        ) : (
                          <Save />
                        )}
                        {field.kind === 'signature' ? 'Sign' : 'Save'}
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </section>

            <aside className="space-y-3">
              <form
                onSubmit={submitComment}
                className="space-y-2 rounded-md border border-border bg-card p-3"
                data-testid="public-comments"
              >
                <p className="flex items-center gap-2 text-sm font-medium">
                  <MessageSquare className="size-4" aria-hidden />
                  Comments
                </p>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  className="min-h-24 w-full rounded-md border border-input bg-card px-3 py-2 text-sm shadow-soft outline-none transition-[box-shadow,border-color] focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/30"
                  placeholder="Leave a comment for the sender"
                  data-testid="public-comment-input"
                />
                <Button
                  type="submit"
                  size="sm"
                  disabled={!comment.trim() || savingComment}
                  data-testid="public-comment-submit"
                >
                  {savingComment && <Loader2 className="animate-spin" />}
                  Comment
                </Button>
              </form>

              <div className="space-y-2">
                {(payload?.comments ?? []).map((event) => (
                  <div
                    key={event.uuid}
                    className="rounded-md border border-border bg-muted/30 px-3 py-2 text-sm"
                    data-testid={`public-comment-${event.uuid}`}
                  >
                    {commentBody(event)}
                  </div>
                ))}
              </div>
            </aside>
          </div>
        )}
      </div>
    </main>
  )
}
